import React, { useState } from "react";
import { Link } from "react-router-dom";
import { HandCoins, Percent, Gift, TrendingUp, ArrowLeft, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { affiliateApplicationsApi } from "../lib/api";
import { SEO } from "../components/seo/SEO";
import { getPageSeo } from "../lib/seoConfig";
import { trackEvent } from "../lib/analytics";
import { useNavigationHistory } from "../contexts/NavigationHistoryContext";

const LOGO = "/brand-logo.png";
const NAVY = "bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]";
const btnPrimary =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[14px] font-semibold px-5 py-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:translate-y-0";
const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 placeholder:text-slate-400";
const labelCls = "block text-[12px] font-semibold text-slate-700 mb-1.5";

const PERKS = [
  { icon: Percent, title: "Recurring residual %", body: "Earn a percentage on every subscription renewal your referrals make — for as long as they stay a customer." },
  { icon: TrendingUp, title: "First-purchase commission", body: "Get paid the moment a referral converts to a paid plan, and again whenever they add seats." },
  { icon: Gift, title: "A perk for your audience", body: "Everyone you refer gets an extended free trial plus a discount on their first AI credit purchase." },
];

export default function BecomeAffiliate() {
  const { goBack } = useNavigationHistory();
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) { toast.error("Please add your name and email."); return; }
    setBusy(true);
    try {
      await affiliateApplicationsApi.create(f);
      setDone(true);
      trackEvent("generate_lead", { lead_type: "affiliate_application" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO title={getPageSeo("/become-an-affiliate").title} description={getPageSeo("/become-an-affiliate").description} path="/become-an-affiliate" />
      <header className={`${NAVY} text-white`}>
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="affiliate-apply-home-link">
            <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center overflow-hidden"><img src={LOGO} alt="ServiceGuru Pro" className="h-full w-full object-contain" /></div>
            <span className="font-bold text-[15px] tracking-tight">ServiceGuru Pro</span>
          </Link>
          <button onClick={() => goBack("/")} className="inline-flex items-center gap-1.5 text-[13px] text-slate-300 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
          </button>
        </div>
        <div className="max-w-5xl mx-auto px-5 lg:px-8 pb-16 pt-6 text-center">
          <div className="h-14 w-14 rounded-2xl bg-[#FFB800] flex items-center justify-center mx-auto shadow-lg shadow-amber-500/20"><HandCoins className="h-7 w-7 text-[#0F172A]" /></div>
          <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight">Become an Affiliate</h1>
          <p className="mt-4 text-slate-300 text-lg max-w-xl mx-auto">Refer contractors to ServiceGuru Pro and earn commission on every purchase — plus a recurring cut of every renewal.</p>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 -mt-10 pb-20">
        <div className="grid lg:grid-cols-3 gap-4 mb-10">
          {PERKS.map((p) => (
            <div key={p.title} className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
              <p.icon className="h-5 w-5 text-[#2563EB]" />
              <div className="mt-3 font-bold text-slate-900 text-[15px]">{p.title}</div>
              <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>

        <div className="max-w-xl mx-auto rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className={`${NAVY} text-white px-6 py-5`}>
            <div className="font-bold text-[17px] tracking-tight">Apply to become an affiliate</div>
            <p className="text-slate-300 text-[13px] mt-1">We review every application personally — you'll hear back with your coupon code and commission terms.</p>
          </div>
          {done ? (
            <div className="p-8 text-center" data-testid="affiliate-apply-success">
              <div className="mx-auto h-14 w-14 rounded-full bg-blue-50 text-[#2563EB] flex items-center justify-center"><Check className="h-7 w-7" /></div>
              <h3 className="mt-4 text-xl font-bold tracking-tight text-slate-900">Application received!</h3>
              <p className="mt-2 text-slate-600 text-[14px]">Thanks{f.name ? `, ${f.name.split(" ")[0]}` : ""} — our team will review it and reach out shortly.</p>
              <button onClick={() => goBack("/")} data-testid="affiliate-apply-done" className={`${btnPrimary} mt-6`}>Back to home</button>
            </div>
          ) : (
            <form onSubmit={submit} className="p-6 space-y-4">
              <div><label className={labelCls}>Full name *</label><input data-testid="affiliate-apply-name" value={f.name} onChange={set("name")} className={inputCls} placeholder="Jordan Smith" /></div>
              <div><label className={labelCls}>Email *</label><input data-testid="affiliate-apply-email" type="email" value={f.email} onChange={set("email")} className={inputCls} placeholder="you@partner.com" /></div>
              <div><label className={labelCls}>Phone</label><input data-testid="affiliate-apply-phone" value={f.phone} onChange={set("phone")} className={inputCls} placeholder="+1 555 123 4567" /></div>
              <div><label className={labelCls}>How will you promote us?</label><textarea data-testid="affiliate-apply-message" value={f.message} onChange={set("message")} rows={3} className={inputCls} placeholder="Newsletter, YouTube channel, trade association, contractor community…" /></div>
              <button type="submit" disabled={busy} data-testid="affiliate-apply-submit" className={`${btnPrimary} w-full`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <HandCoins className="h-4 w-4" />} Submit application
              </button>
              <p className="text-[11px] text-slate-400 text-center">Already approved? <Link to="/affiliate/login" className="text-[#2563EB] hover:underline">Sign in to your affiliate portal</Link>.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
